import { Button } from '@mui/material';
import { Edit, History, Settings2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useOrganization } from '../hooks/useOrganization';
import ConfigureDataFeedModal from '../components/Layout/ConfigureDataFeedModal';
import ConfirmModal from '../components/Layout/ConfirmModal';

const DataFeed = () => {
  const { accountSelected } = useOrganization();
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [dataFeedConfigs, setDataFeedConfigs] = useState([]);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [deletingConfigId, setDeletingConfigId] = useState(null);

  const handleConfigureOpen = () => {
    setEditingConfigId(null);
    setShowConfigureModal(true);
  };

  const handleConfigureClose = () => {
    setEditingConfigId(null);
    setShowConfigureModal(false);
  };

  const handleEditOpen = (configId) => {
    setEditingConfigId(configId);
    setShowConfigureModal(true);
  };

  const handleDeleteOpen = (configId) => {
    setDeletingConfigId(configId);
  };

  const handleDeleteCancel = () => {
    setDeletingConfigId(null);
  };

  const handleDeleteConfirm = () => {
    if (!deletingConfigId) {
      return;
    }

    setDataFeedConfigs((currentConfigs) =>
      currentConfigs.filter((config) => config.id !== deletingConfigId)
    );
    setDeletingConfigId(null);
  };

  const formatFrequency = (value) => {
    if (!value) {
      return '';
    }

    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const handleConfigureSave = (config) => {
    if (editingConfigId) {
      setDataFeedConfigs((currentConfigs) =>
        currentConfigs.map((currentConfig) =>
          currentConfig.id === editingConfigId
            ? { ...currentConfig, ...config }
            : currentConfig
        )
      );
      return;
    }

    setDataFeedConfigs((currentConfigs) => [
      {
        id: `${accountSelected}-${Date.now()}`,
        accountId: accountSelected,
        ...config,
      },
      ...currentConfigs,
    ]);
  };

  const visibleConfigs = dataFeedConfigs.filter((config) => config.accountId === accountSelected);
  const editingConfig = dataFeedConfigs.find((config) => config.id === editingConfigId) || null;

  if (!accountSelected) {
    return (
      <div className="main-content page-dashboard">
        <p style={{ textAlign: 'center', marginTop: 50, fontSize: '18px', fontWeight: 'bold' }}>
          Please select an account to view the data feed.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="main-content page-dashboard" style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 20, marginTop: 10, padding: '0 4px' }}>
          <div style={{ flex: 1, maxWidth: 360 }} />
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfigureOpen}
          >
            <Settings2 className="nav-icon" style={{ marginRight: 11 }} size={16} /> Configure FTP/SFTP
          </Button>
        </div>

        <div className="data-feed-panel">
          <div className="data-feed-table-wrapper">
            <table className="data-feed-table">
              <thead>
                <tr className="data-feed-table__header-row">
                  <th className="data-feed-table__header-cell">
                    Host / Destination / Source
                  </th>
                  <th className="data-feed-table__header-cell">
                    Username
                  </th>
                  <th className="data-feed-table__header-cell">
                    Keyfile
                  </th>
                  <th className="data-feed-table__header-cell">
                    Remote Directory
                  </th>
                  <th className="data-feed-table__header-cell">
                    Archive Directory
                  </th>
                  <th className="data-feed-table__header-cell">
                    Filename Pattern
                  </th>
                  <th className="data-feed-table__header-cell">
                    Frequency
                  </th>
                  <th className="data-feed-table__header-cell" />
                </tr>
              </thead>
              <tbody>
                {visibleConfigs.length > 0 ? (
                  visibleConfigs.map((config) => (
                    <tr key={config.id}>
                      <td className="data-feed-table__cell">{config.host}</td>
                      <td className="data-feed-table__cell">{config.username}</td>
                      <td className="data-feed-table__cell">{config.keyfileName || config.keyfile || ''}</td>
                      <td className="data-feed-table__cell">{config.remoteDirectory}</td>
                      <td className="data-feed-table__cell">{config.archiveDirectory}</td>
                      <td className="data-feed-table__cell">{config.filenamePattern}</td>
                      <td className="data-feed-table__cell">{formatFrequency(config.frequency)}</td>
                      <td className="data-feed-table__cell">
                        <div className="data-feed-table__action-group">
                          <button
                            className="data-feed-table__action-button"
                            type="button"
                            onClick={() => handleEditOpen(config.id)}
                            aria-label="Edit data feed row"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="data-feed-table__action-button"
                            type="button"
                            onClick={() => handleDeleteOpen(config.id)}
                            aria-label="Delete data feed row"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                          <button
                            className="data-feed-table__action-button"
                            type="button"
                            aria-label="History"
                            title="History"
                          >
                            <History size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="data-feed-table__empty-cell">
                      No data feeds configured yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showConfigureModal && (
        <ConfigureDataFeedModal
          onClose={handleConfigureClose}
          onSave={handleConfigureSave}
          initialValues={editingConfig}
          isEdit={Boolean(editingConfig)}
        />
      )}

      {deletingConfigId && (
        <ConfirmModal
          title="Delete configuration?"
          description="Are you sure you want to delete this data feed configuration?"
          cancelLabel="No"
          confirmLabel="Yes"
          confirmColor="error"
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default DataFeed;
